/**
 * 매핑 검증 issue → 사용자 문장과 기술 정보.
 *
 * 코드 → 문장을 이 파일 **한 곳**이 소유한다(규칙 §11). BE 원문(영어)은 그대로 보이지 않고 기술 정보로만
 * 접어 둔다(규칙 §5). 모르는 코드(엔진이 원본에서 찾은 결함 등)는 일반 문장으로 떨어진다.
 *
 * Owns:
 * - issue 코드의 문장, 기술 정보 줄.
 *
 * Does not own:
 * - 경로의 이름 — `mappingPath.ts`. 경로를 폼 칸에 붙이는 일 — `issuePath.ts`.
 */
import type { MappingIssue } from "../domain/mapping";
import { QUATERNION_MIN_NORM } from "./rotationMath";

function rowText(issue: MappingIssue): string {
  return issue.row === null ? "" : ` 행 ${issue.row} 에서`;
}

/** issue 한 줄의 문장. 어느 칸의 문제인지는 호출자가 경로 이름으로 붙인다. */
export function mappingIssueText(issue: MappingIssue): string {
  switch (issue.code) {
    case "hands.invalid":
      return "손 구성이 올바르지 않습니다. 한 손(single) 하나 또는 왼손 · 오른손을 겹치지 않게 골라 주세요.";
    case "mapping.incomplete":
      return "필수 값이 비어 있습니다. 값을 채워 주세요.";
    case "cameras.invalid":
      return "관측 카메라를 하나 이상 고르고, slot 에 서로 다른 영문 · 숫자 이름을 적어 주세요.";
    case "camera.missing":
      return "이 episode 에 이 카메라의 원본이 없습니다.";
    case "pose.clock_mismatch":
      return "위치 채널과 자세 채널이 같은 시계를 선언하지 않았습니다. 한 자세로 합칠 수 없습니다.";
    case "pose.row_count_mismatch":
      return "위치 채널과 자세 채널의 행 수가 다릅니다. 한 자세로 합칠 수 없습니다.";
    case "pose.timestamp_mismatch":
      return `위치 채널과 자세 채널의 기록 시각이${rowText(issue)} 다릅니다. 보간하지 않으므로 한 자세로 합칠 수 없습니다.`;
    case "pose.reinterpretation_required":
      return "원본 선언과 다르게 해석했습니다. 재해석 사유를 적어 주세요.";
    case "pose.reinterpreted":
      return "원본 선언과 다르게 해석했습니다. 적은 사유와 함께 경고로 남깁니다.";
    case "reference.transform_unknown":
      return "기준 변환을 고정으로 골랐지만 이 칸이 비어 있습니다. 모르면 비워 두고, 값을 확인한 뒤 채워 주세요.";
    case "reference.same_frame":
      return "기준 변환의 원본 프레임이 기준 프레임과 같습니다. 기준 프레임에는 출력 좌표계를 적습니다. 옮기지 않는다면 기준 변환을 없음으로 두어 주세요.";
    case "mapping.values_invalid":
      return "고른 열의 값으로 자세나 그리퍼를 계산하지 못했습니다. 열 · 표현 · 단위를 확인해 주세요.";
    case "mapping.quaternion_sign_mixed":
      return `quaternion 길이가 ${QUATERNION_MIN_NORM} 보다 짧은 행이 있습니다. 원본이 부호가 다른 quaternion 을 섞어 평균한 값이라 정규화해도 자세가 돌아오지 않습니다. 같은 손의 다른 자세 채널을 고르거나 Dataset 조사 사실을 확인해 주세요.`;
    case "mapping.input_invalid":
      return "샘플에서 변환 입력을 만들지 못했습니다. 채널 · 카메라 선택을 확인해 주세요.";
    case "time.clock_mismatch":
      return "고른 출력 채널들이 같은 시계를 선언하지 않았습니다.";
    case "time.empty":
      return "고른 출력 채널에 기록 시각이 없습니다.";
    default:
      return issue.severity === "warning"
        ? "검사에서 경고가 나왔습니다. 기술 정보를 확인해 주세요."
        : "검사를 통과하지 못했습니다. 기술 정보를 확인해 주세요.";
  }
}

/** 접어 둘 기술 정보 줄. 코드 · 원문 · 채널 · 행 · 시각을 있는 것만 적는다. */
export function mappingIssueDetails(issue: MappingIssue): readonly string[] {
  const lines = [`code: ${issue.code}`, `path: ${issue.path}`, `message: ${issue.message}`];
  if (issue.channel !== null) {
    lines.push(
      issue.otherChannel === null
        ? `channel: ${issue.channel}`
        : `channel: ${issue.channel} · ${issue.otherChannel}`,
    );
  }
  if (issue.row !== null) {
    lines.push(`row: ${issue.row}`);
  }
  if (issue.timestamp !== null) {
    const unit = issue.timestampUnit === null ? "" : ` ${issue.timestampUnit}`;
    const other =
      issue.otherTimestamp === null
        ? ""
        : ` · ${issue.otherTimestamp}${issue.otherTimestampUnit === null ? "" : ` ${issue.otherTimestampUnit}`}`;
    lines.push(`timestamp: ${issue.timestamp}${unit}${other}`);
  }
  return lines;
}
